import { DeleteForever } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InfoIcon from '@mui/icons-material/Info';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  SelectChangeEvent,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CgArrowsExchangeAlt } from 'react-icons/cg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShepherdTourContext } from 'react-shepherd';
import {
  ICandidate,
  IJobCandidateInteraction,
  amplitude,
  ICandidateRecentConversation,
  getClosuresBySubmissionIds,
  deleteSubmission,
  IStageStatusPair,
  IUserDTO,
  getSubmissionStageStats,
  getCurrentUserTeams,
} from 'wisestep-api';
import {
  ScrollableTabs,
  TabTextVariant,
  WsPagination,
  IFilterList,
  WsFilterTags,
  WsListingLoader,
  WsTitleBanner,
  WsColors,
  WsDrawer,
  WsDialog,
  WsElementWithPopOver,
  WsIconMoreVer,
  WsIconText,
  WsTypoColors,
  WsTypoVariants,
} from 'wisestep-base';

import { UpdateStatusModal } from '../../../components';
import BulkEmail from '../../../components/BulkEmail';
import BulkSmsDrawer from '../../../components/BulkSms/BulkSmsDrawer';
import BulkUpdateStatusDrawer from '../../../components/BulkUpdateStatusDrawer';
import SubmissionAdvancedFilterFormProps from '../../../components/filters/models/SubmissionAdvanceFilterFormProps';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { WsCandidateCard } from '../../../components/WsCandidateCard';
import { useUserContext, useWorkspaceContext } from '../../../context';
import NoDataMsg from '../../../features/candidates/talentpool/NoDataMsg';
import { ITrackCvPages } from '../../../features/conversationWidget/utils/cvHelper';
import { useListingSettings } from '../../../features/jobs/listing/hooks';
import { ClosureAction, useClosureAuthz } from '../../../hooks/useClosureAuthz';
import ErrorLogger from '../../../providers/ErrorLogger';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { extractEmail } from '../../../utils/StringUtils';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateBulkSelectionBar from '../../candidates/components/candidateBulkSelectionBar';
import ConfirmationDialog from '../../candidates/components/confirmationDialog/ConfirmationDialog';
import {
  AppLayoutTheme,
  LayoutTheme,
  getLocation,
  setDefaultAdvanceFilters,
  apiFailedMessage,
} from '../../candidates/utils/CandidateHelper';
import { ClosureDetailsDTO } from '../../closures/types';
import { handleCandidateDownload } from '../../dataDownload/helper/PayloadHelper';
import { handleSelectedCandidatesLimit } from '../../jobsCrm/components/helper/SendSmsEmailFuntionality';
import StageStatusContextProvider, { useStageStatusContext } from '../context/StageStatusContext';
import useSubmissionListing from '../hooks/useListingCandidates';
import useSubmissionListingStageStats, {
  IAggregation,
} from '../hooks/useSubmissionListingStageStats';

import ConvertPlacement from './components/ConvertPlacement/ConvertPlacement';
import DownloadField from './components/Download/DownloadField';
import ShareCandidateProfileEmail from './components/ShareCandidateProfileEmail';
import SubmissionListSideNav from './components/submissionListSideNav';
import UploadToVMS from './components/UploadToVMS';
import { SubmissionListingDefaultQuery } from './SubmissionListingDefaultQuery';
import SubmissionListingSearchFilter from './submissionListingSearchFilter';
import { SubmissionListingSearchParams } from './submissionListingSearchFilter/SubmissionListingSearchParams';

const submissionTabsList = [
  {
    tabName: 'All Submissions',
  },
  {
    tabName: 'My Submissions',
  },
];

export default function SubmissionListing() {
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const tour = useContext(ShepherdTourContext);
  const vmsFeatureAllowedEmails = [
    'vikranth.y@avanceservices.us',
    'gopi.pogula@avanceservices.co.uk',
    'gopi.pogula@avanceservices.com',
    'wscrm@avanceunlimited.com',
    'dhawal@sharklasers.com',
    'pd@pd.com',
  ];
  const navigateBack = useNavigate();
  const { jobFieldSettings } = useListingSettings();
  const stageStatus = useStageStatusContext();
  const workspace = useWorkspaceContext();
  const isAuthorizedAddPlacement = useClosureAuthz(ClosureAction.ADD_CLOSURE_ADDITION);
  const isAuthorizedViewPlacement = useClosureAuthz(ClosureAction.VIEW_CLOSURE_DETAILS_PLACEMENTS);

  const userContext = useUserContext().userDTO;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryData = searchParams.get('d') ?? '';

  const decodedQueryData: IJobCandidateInteraction = queryData
    ? {
        ...getDecodedQueryParams(queryData),
        candidateStageStatusPipelineData: stageStatus?.stageStatusPipeline ?? '',
      }
    : SubmissionListingDefaultQuery;

  const [query, setQuery] = useState<IJobCandidateInteraction>(decodedQueryData);
  const [loader, setLoader] = useState<boolean>(false);
  const data = useSubmissionListing(query, setLoader, 'submissions');
  const totalPageCount = data.pagesCount < 500 ? data.pagesCount : 500;
  const [closures, setClosures] = useState<ClosureDetailsDTO[]>();
  const [stageStats, allCandidatesCount] = useSubmissionListingStageStats(query);
  const [allStageStats, setAllStageStats] = useState<IAggregation[]>([]);
  const [allCandidatesCountForBD, setAllCandidatesCountForBD] = useState<number>(0);
  const [openStageStats, setOpenStageStats] = useState<IAggregation[]>([]);
  const [closeSageStats, setCloseSageStats] = useState<IAggregation[]>([]);
  const [candidateJobStatusByBD, setCandidateJobStatusByBD] = useState<string>('notSelected');
  const [candidateJobStatusByADV, setCandidateJobStatusByADV] = useState<string>('notSelected');
  const [candidateJobStatusByADVALL, setCandidateJobStatusByADVALL] = useState<boolean>(false);

  useEffect(() => {
    const fetchJobStats = async () => {
      const candidateJobStatus = query.candidateJobStatus;
      const response = await getSubmissionStageStats({
        ...query,
        currentStageCurrentStatusPairs: [],
        candidateJobStatus:
          isEmpty(candidateJobStatus) ||
          !query.jobStatusUpdatedByADVFilter ||
          candidateJobStatusByADV === 'openAndClosed'
            ? 'Any'
            : candidateJobStatus,
      });
      let sum = 0;
      const result = response.data.ApplicationStageStatus.buckets;
      result.forEach((item) => {
        sum = sum + item.doc_count;
      });
      setAllStageStats(result);
      setAllCandidatesCountForBD(sum);
    };
    fetchJobStats();
  }, [query]);

  useEffect(() => {
    const fetchJobStats = async () => {
      const candidateJobStatus = query?.candidateJobStatus;
      const response = await getSubmissionStageStats({
        ...query,
        currentStageCurrentStatusPairs: [],
        candidateJobStatus: 'Opened',
      });
      const result = response.data.ApplicationStageStatus.buckets;
      setOpenStageStats(
        isEmpty(candidateJobStatus) ||
          !query.jobStatusUpdatedByADVFilter ||
          candidateJobStatus === 'Opened' ||
          candidateJobStatus === 'Any' ||
          candidateJobStatusByADV === 'openAndClosed'
          ? result
          : []
      );
    };
    fetchJobStats();
  }, [query]);

  useEffect(() => {
    const fetchJobStats = async () => {
      const candidateJobStatus = query.candidateJobStatus;
      const response = await getSubmissionStageStats({
        ...query,
        currentStageCurrentStatusPairs: [],
        candidateJobStatus: 'Closed',
      });
      const result = response.data.ApplicationStageStatus.buckets;
      setCloseSageStats(
        isEmpty(candidateJobStatus) ||
          !query.jobStatusUpdatedByADVFilter ||
          candidateJobStatus === 'Closed' ||
          candidateJobStatus === 'Any' ||
          candidateJobStatusByADV === 'openAndClosed'
          ? result
          : []
      );
    };
    fetchJobStats();
  }, [query]);

  const [selectedCandiate, setSelectedCandidate] = useState<ICandidate | null>(null);
  const [selectedSubCandiate, setSelectedSubCandidate] = useState<ICandidate | null>(null);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const [title, setTitle] = useState<string>('All Submissions');
  const [tempPageValue, setTempPageValue] = useState<number>(0);
  const formattedDateISO = (date: Date): string => dayjs(date).toISOString();
  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const updateView = useConversationWidgetStore((state) => state.updateView);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [businessDefinitionFilters, setBusinessDefinitionFilters] = useState<IFilterList[]>([]);
  const [stageStatusFilters, setStageStatusFilters] = useState<IFilterList[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(query.page ?? 1);
  const [advancedFilterDefaultValues, setAdvancedFilterFields] =
    useState<SubmissionAdvancedFilterFormProps>(setDefaultAdvanceFilters(query) ?? {});
  const setNotification = useNotificationStore((state) => state.setNotification);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const [searchFilterDefaultValues, setSearchFilterDefaultValues] =
    useState<SubmissionListingSearchParams>({
      query: query.query ?? '',
      accountMangers: query.jobAccountManagerEmails ?? [],
      fromDate: query.updatedFromDate as Date,
      toDate: query.updatedToDate as Date,
      candidateStatus: query.activeStatus,
      sort: query.sort?.[0] ?? '_score,desc',
      recruiterActions: query.recruiterActions ?? [],
      candidateResponses: query.candidateResponses ?? [],
      dateFilterStatus: query.datesUnionBy ?? 'CREATED_OR_UPDATED_BETWEEN',
    });
  const [openDownload, setOpenDownload] = useState<boolean>(false);
  const [type, setType] = useState<string>('');
  const [isStageStatus, setIsStageStatus] = useState<boolean>(true);
  const [openPlacementDrawer, setOpenPlacementDrawer] = useState<boolean>(false);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const openUpdateStatusModal = () => setOpenStatusModal(true);

  const closeUpdateStatusModal = () => {
    data.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
    setOpenStatusModal(false);
  };

  const [openBulkStatusModal, setOpenBulkStatusModal] = useState<boolean>(false);
  const openBulkUpdateStatusModal = () => setOpenBulkStatusModal(true);

  const closeBulkUpdateStatusModal = () => {
    data.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
    setOpenBulkStatusModal(false);
  };

  const routerNavigate = useNavigate();

  useEffect(() => {
    (async () => {
      const response = await getClosuresBySubmissionIds(data.list.map(({ id }) => id as string));
      setClosures(response.data);
    })();
  }, [data.list]);

  useEffect(() => {
    if (advancedFilterDefaultValues) {
      pushSearchFilters(advancedFilterDefaultValues);
    }
    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= data.list.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        // updateView('submission');
        setCvIndex(index);
        // openModal(data.list[index]);
      } else if (cvIndex >= 0 && cvIndex < data.list.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        // updateView('submission');
        setCvIndex(index);
        // openModal(data.list[index]);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    advancedFilterDefaultValues,
    cvIndex,
    data.list,
    navigate,
    navigationState,
    openModal,
    updateNavigationState,
    updateView,
  ]);

  const [updateAllowed, setUpdateAllowed] = useState(false);
  const [deleteBDFilterIndex, setDeleteBDFilterIndex] = useState<boolean>(false);
  const [deleteCSFilterIndex, setDeleteCSFilterIndex] = useState<boolean>(false);

  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const selectedlist = data.list.filter((i) => i.isSelected);

  const [teamIds, setteamIds] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const allTeams = await getCurrentUserTeams();
      const teamIds = [
        ...(allTeams.data?.belongsToTeams ?? []),
        ...(allTeams.data?.ownsTeams ?? []),
      ];
      setteamIds(teamIds);
    })();
  }, []);

  useEffect(() => {
    if (selectedlist.length === 0) {
      setUpdateAllowed(false);
      return;
    }

    const emails = [loggedInUser?.email, ...(loggedInUser?.oldEmails ?? [])].filter(Boolean);

    const results = selectedlist.map(
      (candidate) =>
        !candidate.isInDnc &&
        candidate.jobId &&
        (workspace?.owns ||
          emails.includes(candidate.jobWorkAssignment?.createdBy) ||
          teamIds.includes(candidate.jobPostedByTeam?.id ?? ''))
    );

    setUpdateAllowed(results.every((result) => result));
  }, [selectedlist, loggedInUser, workspace, teamIds]);

  const submissionAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'Submissions',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  const encodeQueryParams = (data) => {
    //skipping candidateStageStatusPipelineData before encoding to url and resetting on load to prevent connection loss error due to large url
    return getEncodedQueryParams({ ...data, candidateStageStatusPipelineData: '' });
  };

  const onSearch = (data: SubmissionListingSearchParams) => {
    submissionAmplitudeEvent('Submissions_Search', 'Search');
    setNumberOfSelectedCandidates(0);
    // setSearchFilterDefaultValues(data);
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    setPageNumber(1);
    updatedQuery.query = data.query ?? '';
    updatedQuery.jobAccountManagerEmails =
      data.accountMangers?.map((email) => extractEmail(email)) ?? [];
    typeof data.fromDate != 'string' && data.fromDate?.setHours(0, 0, 0);
    typeof data.toDate != 'string' && data.toDate?.setHours(23, 59, 29);
    updatedQuery.updatedFromDate = data.fromDate ? formattedDateISO(data.fromDate) : '';
    updatedQuery.updatedToDate = data.toDate ? formattedDateISO(data.toDate) : '';
    updatedQuery.activeStatus = data.candidateStatus ?? null;
    updatedQuery.sort = [data.sort ?? 'createdOn,desc'];
    updatedQuery.recruiterActions = data.recruiterActions;
    updatedQuery.candidateResponses = data.candidateResponses;
    if (data.query !== undefined && data.query.length > 0) {
      const srt = data.sort ? data.sort.split(',') : ['desc'];
      updatedQuery.sort = [`_score,${srt[1] ?? 'desc'}`];
    } else {
      updatedQuery.sort = [data.sort ?? 'createdOn,desc'];
    }
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    // updatedQuery.auditQuery = true;
    updatedQuery.candidateStageStatusPipelineData = stageStatus?.stageStatusPipeline ?? '';
    updatedQuery.datesUnionBy = data.dateFilterStatus ?? 'CREATED_OR_UPDATED_BETWEEN';
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    setSearchQuery(updatedQuery.query ?? '');
  };

  const pushSearchFilters = (data: SubmissionAdvancedFilterFormProps) => {
    const filtersArray: IFilterList[] = [];
    const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
    if (data.candidateLocations?.length)
      filtersArray.push({
        tag: `Candidate Locations: ${data.candidateLocations
          .map((location) => `${location.city}-${location.state}-${location.country}`)
          .join(',')}`,
        id: 'candidateLocations',
      });

    if (data.clientNames?.length)
      filtersArray.push({ tag: `Clients: ${data.clientNames.join(',')}`, id: 'clientNames' });
    if (data.clientRecruiters && data.clientRecruiters.length > 0)
      filtersArray.push({
        tag: `Client Recruiters: ${data.clientRecruiters.join(',')}`,
        id: 'clientRecruiters',
      });
    if (!isEmpty(data.jobStatus))
      filtersArray.push({ tag: `Job Status: ${data.jobStatus}`, id: 'jobStatus' });
    if (data.jobTitles?.length)
      filtersArray.push({ tag: `Job Title: ${data.jobTitles.join(',')}`, id: 'jobTitles' });
    if (data.jobType) filtersArray.push({ tag: `Job Type: ${data.jobType}`, id: 'jobType' });
    if (data.jobLocations?.length)
      filtersArray.push({
        tag: `Job Locations: ${data.jobLocations
          .map((location) => getLocation(location))
          .join(',')}`,
        id: 'jobLocations',
      });
    if (!isEmpty(data.relocationPreference))
      filtersArray.push({
        tag: `Relocation: ${data.relocationPreference}`,
        id: 'relocationPreference',
      });
    if (!isEmpty(data.submissionsToJobsPostedByTeamMemberAutocompleteSelects))
      filtersArray.push({
        tag: `Submission to Jobs posted by member: ${data.submissionsToJobsPostedByTeamMemberAutocompleteSelects}`,
        id: 'submissionsToJobsPostedByTeamMemberAutocompleteSelects',
      });
    if (!isEmpty(data.submissionsToJobsPostedByTeamNameAutocompleteSelects))
      filtersArray.push({
        tag: `Submission to Jobs Posted by name: ${data?.submissionsToJobsPostedByTeamNameAutocompleteSelects
          ?.map((obj) => obj.name)
          .join(',')}`,
        id: 'submissionsToJobsPostedByTeamNameAutocompleteSelects',
      });
    if (!isEmpty(data.submittedByRecruiterAutocompleteSelects))
      filtersArray.push({
        tag: `Submitted By Recruiter: ${data.submittedByRecruiterAutocompleteSelects}`,
        id: 'submittedByRecruiterAutocompleteSelects',
      });
    if (!isEmpty(data.submittedByRecruiterIds) && data.submittedByRecruiterIds?.length)
      filtersArray.push({
        tag: `Submitted By Recruiter id: ${data.submittedByRecruiterIds.join(',')}`,
        id: 'submittedByRecruiterIds',
      });
    if (!isEmpty(data.tags) && data.tags?.length)
      filtersArray.push({ tag: `Tags: ${data.tags.join(',')}`, id: 'tags' });
    if (!isEmpty(data.teamNameAutocompleteSelects))
      filtersArray.push({
        tag: `Teams: ${data.teamNameAutocompleteSelects?.map((obj) => obj.name).join(',')}`,
        id: 'teamNameAutocompleteSelects',
      });
    if (!isEmpty(data.teams) && data.teams?.length)
      filtersArray.push({
        tag: `Teams: ${data.teams.map((obj) => obj.name).join(',')}`,
        id: 'teams',
      });
    if (data.candidateNotesLatestCreatedAtFromDate)
      filtersArray.push({
        tag: `Created (from Date): ${formattedDate(
          data.candidateNotesLatestCreatedAtFromDate.toString() ?? ''
        )}`,
        id: 'candidateNotesLatestCreatedAtFromDate',
      });
    if (data.candidateNotesLatestCreatedAtToDate)
      filtersArray.push({
        tag: `Created (to Date): ${formattedDate(
          data.candidateNotesLatestCreatedAtToDate?.toString() ?? ''
        )}`,
        id: 'candidateNotesLatestCreatedAtToDate',
      });
    if (data.salaryRange?.currency)
      filtersArray.push({
        tag: `${data.salaryRange.min ?? ''} to ${data.salaryRange.max ?? ''} 
        ${data.salaryRange.currency ?? ''} 
        ${data.salaryRange.unitTime ?? ''}`,
        id: 'salaryRange',
      });
    if (data.tat?.min && data.tat?.max)
      filtersArray.push({
        tag: `Hour's fo Job posting from ${data.tat?.min ?? ''} to ${data.tat?.max ?? ''}`,
        id: 'tat',
      });

    if (data.stageDateFromDate && data.stageDateToDate)
      filtersArray.push({
        tag: `Stage Date Between: ${formattedDate(
          data.stageDateFromDate.toString() ?? ''
        )} and ${formattedDate(data.stageDateToDate?.toString() ?? '')}`,
        id: 'stageDateFilter',
      });
    if (!isEmpty(data.businessUnit)) {
      const hyphenIndex = Number(data.businessUnit?.indexOf('&'));
      const name: string =
        hyphenIndex !== -1
          ? data.businessUnit?.substring(hyphenIndex + 1).trim() ?? ''
          : data.businessUnit?.trim() ?? '';

      filtersArray.push({ tag: `Business Unit: ${name}`, id: 'businessUnit' });
    }
    if (!isEmpty(data.businessDefinitionName))
      pushBusinessDefinitionTag(data.businessDefinitionName);

    setAppliedFilters(filtersArray);
    isStageStatus && pushStageAndStatusTags(data.stageStatus ?? []);
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: SubmissionAdvancedFilterFormProps = {
        ...advancedFilterDefaultValues,
        stageStatus: query.stageStatusPairs,
        businessDefinitionData: query.businessDefinitionData,
      };
      if (
        [
          'jobTitles',
          'tags',
          'clientNames',
          'jobLocations',
          'candidateLocation',
          'clientRecruiters',
        ].includes(idOfChip)
      )
        advancedFilterDefaults[idOfChip] = [];
      else if (idOfChip == 'stageDateFilter') {
        advancedFilterDefaults.stageDateFromDate = undefined;
        advancedFilterDefaults.stageDateToDate = undefined;
      } else if (idOfChip == 'jobStatus') {
        advancedFilterDefaults.jobStatusUpdatedByADVFilter = false;
        advancedFilterDefaults[idOfChip] = null;
        setCandidateJobStatusByADV('notSelected');
        setCandidateJobStatusByADVALL(false);
        advancedFilterDefaults['candidateJobStatus'] =
          candidateJobStatusByBD != 'notSelected' ? candidateJobStatusByBD : 'notSelected';
      } else advancedFilterDefaults[idOfChip] = null;
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setSearchParams({ d: encodeQueryParams(updateQuery(advancedFilterDefaults)) });
      setQuery(updateQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };

  const onDeleteBusinessFilterTag = (tag: string) => {
    setBusinessDefinitionFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: SubmissionAdvancedFilterFormProps = advancedFilterDefaultValues;
      if (
        [
          'jobTitles',
          'tags',
          'clientNames',
          'jobLocations',
          'candidateLocation',
          'clientRecruiters',
        ].includes(idOfChip)
      )
        advancedFilterDefaults[idOfChip] = [];
      else if (idOfChip == 'stageDateFilter') {
        advancedFilterDefaults.stageDateFromDate = undefined;
        advancedFilterDefaults.stageDateToDate = undefined;
      } else if (idOfChip == 'businessDefinitionName') {
        advancedFilterDefaults[idOfChip] = null;
        advancedFilterDefaults['businessDefinitionData'] = [];
        setCandidateJobStatusByBD('notSelected');
        advancedFilterDefaults['candidateJobStatus'] =
          candidateJobStatusByADV != 'notSelected' ? candidateJobStatusByADV : 'notSelected';
      } else advancedFilterDefaults[idOfChip] = null;
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setSearchParams({ d: encodeQueryParams(updateQuery(advancedFilterDefaults)) });
      setQuery(updateQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      setDeleteBDFilterIndex(!deleteBDFilterIndex);
      return [...prev];
    });
  };

  const onDeletestageStatusFilterTag = (tag: string) => {
    const advancedFilterDefaults: SubmissionAdvancedFilterFormProps = advancedFilterDefaultValues;
    setStageStatusFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const status = tag.split(': ')[1];
      if (idOfChip == 'applicationStage') {
        const filteredApplicationStagePairs = query?.currentStageCurrentStatusPairs?.filter(
          (item) => status !== item.applicationStage
        );
        const stageStatus = query?.stageStatusPairs?.filter(
          (item) => status !== item.applicationStage
        );

        pushStageAndStatusTags(stageStatus ?? []);
        setQuery((prevQuery) => ({
          ...prevQuery,
          currentStageCurrentStatusPairs:
            filteredApplicationStagePairs?.length ?? 0 > 0
              ? filteredApplicationStagePairs
              : query?.businessDefinitionData,
          stageStatusPairs: [],
        }));
        setSearchParams({ d: encodeQueryParams({ ...query }) });
        advancedFilterDefaults.stageStatus = stageStatus;
        setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
        setDeleteCSFilterIndex(!deleteCSFilterIndex);
        return prev;
      }
      const filteredPairs = query?.currentStageCurrentStatusPairs?.filter(
        (item) => item?.applicationState && !tag.includes(item?.applicationState)
      );
      setQuery((prevQuery) => ({
        ...prevQuery,
        currentStageCurrentStatusPairs: filteredPairs,
      }));
      setSearchParams({ d: encodeQueryParams({ ...query }) });
      advancedFilterDefaults.stageStatus = filteredPairs;
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      return prev.filter((existChip) => existChip.tag !== tag);
    });
  };

  const updateQuery = (data: SubmissionAdvancedFilterFormProps) => {
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    setPageNumber(1);
    updatedQuery.jobTitles = data.jobTitles ?? [];
    updatedQuery.tags = data.tags;
    updatedQuery.relocationPreference = data.relocationPreference;
    updatedQuery.clientNames = data.clientNames;
    updatedQuery.jobType = data.jobType ?? null;
    updatedQuery.jobLocations = data.jobLocations ?? [];
    updatedQuery.candidateLocations = data.candidateLocations;
    updatedQuery.jobClientRecruiterEmails = data.clientRecruiters;
    updatedQuery.salaryCurrency = data.salaryRange?.currency;
    updatedQuery.minSalary = data.salaryRange?.min;
    updatedQuery.maxSalary = data.salaryRange?.max;
    updatedQuery.salaryTimeSpan = data.salaryRange?.unitTime;
    if (
      candidateJobStatusByBD != data.jobStatus &&
      data.jobStatus != 'openAndClosed' &&
      data.jobStatus != null
    ) {
      if (
        (candidateJobStatusByBD == 'Opened' && data.jobStatus == 'Closed') ||
        (candidateJobStatusByBD == 'Closed' && data.jobStatus == 'Opened')
      ) {
        updatedQuery.currentStageCurrentStatusPairs = [
          {
            applicationStage: 'Dummy',
            applicationState: 'Dummy',
          },
        ];
      } else if (
        (candidateJobStatusByBD == 'Any' && data.jobStatus == 'Closed') ||
        (candidateJobStatusByBD == 'Any' && data.jobStatus == 'Open')
      ) {
        updatedQuery.currentStageCurrentStatusPairs = [
          ...(data.stageStatus ?? []),
          ...(data.businessDefinitionData ?? []),
        ];
      }
    } else if (data.jobStatus == 'openAndClosed') {
      updatedQuery.candidateJobStatus = candidateJobStatusByBD;
      updatedQuery.currentStageCurrentStatusPairs = [
        ...(data.stageStatus ?? []),
        ...(data.businessDefinitionData ?? []),
      ];
    } else {
      updatedQuery.currentStageCurrentStatusPairs = [
        ...(data.stageStatus ?? []),
        ...(data.businessDefinitionData ?? []),
      ];
    }
    updatedQuery.businessDefinitionData = data.businessDefinitionData ?? [];
    updatedQuery.candidateNotesLatestCreatedAtFromDate =
      data.candidateNotesLatestCreatedAtFromDate ?? null;
    updatedQuery.candidateNotesLatestCreatedAtToDate = data.candidateNotesLatestCreatedAtToDate;
    updatedQuery.submittedByRecruiterIds = data.submittedByRecruiterAutocompleteSelects;
    updatedQuery.submittedAsTeamMemberEmails =
      data.submissionsToJobsPostedByTeamMemberAutocompleteSelects;
    updatedQuery.searchAcrossJobPostingTeams = [];
    data.submissionsToJobsPostedByTeamNameAutocompleteSelects?.forEach((obj) =>
      updatedQuery.searchAcrossJobPostingTeams?.push(obj.id ?? '')
    );
    updatedQuery.searchAcrossTeams = [];
    data.teamNameAutocompleteSelects?.forEach((obj) =>
      updatedQuery.searchAcrossTeams?.push(obj.id ?? '')
    );
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    if (data.jobStatus == 'Opened' || data.jobStatus == 'Closed')
      updatedQuery.candidateJobStatus = data.jobStatus;
    else if (data.jobStatus == 'openAndClosed')
      candidateJobStatusByBD == 'notSelected'
        ? (updatedQuery.candidateJobStatus = 'Any')
        : candidateJobStatusByBD;
    else if (data.jobStatus == null) {
      if (!isEmpty(data.candidateJobStatus))
        updatedQuery.candidateJobStatus = data.candidateJobStatus;
      else delete updatedQuery.candidateJobStatus;
    }
    if (data.candidateJobStatus == 'openAndClosed') updatedQuery.candidateJobStatus = 'Any';
    if (data.candidateJobStatus == 'notSelected') delete updatedQuery.candidateJobStatus;
    updatedQuery.jobStatusUpdatedByADVFilter = data.jobStatusUpdatedByADVFilter;
    updatedQuery.timeToServiceBetween = {
      from: data.tat?.min ?? null,
      to: data.tat?.max ?? null,
    };
    if (data.stageDateFromDate && data.stageDateToDate) {
      const fromDate = new Date(data.stageDateFromDate);
      const toDate = new Date(data.stageDateToDate);

      fromDate.setHours(0, 0, 0);
      toDate.setHours(23, 59, 59);

      updatedQuery.stageDateFromDate = formattedDateISO(fromDate);
      updatedQuery.stageDateToDate = formattedDateISO(toDate);
    } else {
      updatedQuery.stageDateFromDate = '';
      updatedQuery.stageDateToDate = '';
    }
    updatedQuery.buId = getId(data.businessUnit ?? '');

    return updatedQuery;
  };
  const getId = (bu: string) => {
    if (!isEmpty(bu)) {
      const hyphenIndex = Number(bu?.indexOf('&'));
      const id: string =
        hyphenIndex !== -1 ? bu?.substring(0, hyphenIndex).trim() ?? '' : bu?.trim() ?? '';
      return id;
    } else {
      return '';
    }
  };
  const onApplyFilters = (data: SubmissionAdvancedFilterFormProps) => {
    const newData = {
      ...data,
      candidateJobStatus: query.candidateJobStatus ?? query.jobStatus ?? '',
      businessDefinitionData: query.businessDefinitionData ?? [],
      jobStatusUpdatedByADVFilter: !isEmpty(data.jobStatus) ? true : false,
    };
    setCandidateJobStatusByADV(data?.jobStatus ?? 'notSelected');
    setCandidateJobStatusByADVALL(data?.jobStatus === 'openAndClosed' ? true : false);
    submissionAmplitudeEvent('Submission_Apply_Filters', 'Apply');
    setSearchParams({ d: encodeQueryParams(updateQuery(newData)) });
    setQuery(updateQuery(newData));
    pushSearchFilters(data);
    pushStageAndStatusTags(
      data?.stageStatus && data?.stageStatus.length > 0
        ? data?.stageStatus
        : query.stageStatusPairs && query.stageStatusPairs.length > 0
        ? query.stageStatusPairs
        : []
    );
    setAdvancedFilterFields(newData);
  };
  const [openDelete, setOpenDelete] = useState(false);

  const pageChange = (event, value) => {
    submissionAmplitudeEvent(
      value < tempPageValue
        ? 'Submission_View_Submission_Previous'
        : 'Submission_View_Submission_Next',
      value < tempPageValue ? 'Previous Button' : 'Next Button'
    );
    setTempPageValue(value);
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    setPageNumber(value + 1);
    setSearchQuery(updatedQuery.query ?? '');
  };
  const selectedIndex = (index: number, value: string) => {
    const updatedQuery = { ...query };
    const stageStatusPair: IStageStatusPair[] = [];
    stageStatusPair.push({ applicationStage: value, applicationState: null });
    updatedQuery.currentStageCurrentStatusPairs = stageStatusPair ?? [];
    pushStageAndStatusTags(stageStatusPair);
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
  };

  const selectedStageStatus = (stage: string, status: string, count: number) => {
    const advancedFilterDefaults: SubmissionAdvancedFilterFormProps = advancedFilterDefaultValues;
    const oldStatus = query.currentStageCurrentStatusPairs?.filter(
      (item) => item.applicationState != null
    );
    const updatedQuery = { ...query };
    updatedQuery.stageStatusPairs = [{ applicationStage: stage, applicationState: status }];
    const stageStatusPair: IStageStatusPair[] = [];
    stageStatusPair.push({ applicationStage: stage, applicationState: status });
    updatedQuery.currentStageCurrentStatusPairs = [
      ...(stageStatusPair ?? []),
      ...(oldStatus ?? []),
    ];
    const filteredData = updatedQuery.currentStageCurrentStatusPairs?.filter(
      (item) =>
        !query.stageStatusPairs?.some(
          (removeItem) =>
            item.applicationStage === removeItem.applicationStage &&
            item.applicationState === removeItem.applicationState
        )
    );
    count > 0
      ? (updatedQuery.currentStageCurrentStatusPairs = filteredData)
      : (updatedQuery.currentStageCurrentStatusPairs = [
          {
            applicationStage: 'Dummy',
            applicationState: 'Dummy',
          },
        ]);
    pushStageAndStatusTags(stageStatusPair);
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    advancedFilterDefaults.stageStatus = stageStatusPair;
    setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
  };

  const pushStageAndStatusTags = (stageStatusPair: IStageStatusPair[]) => {
    const stageTags: Set<string> = new Set();
    const statusTags: Set<string> = new Set();
    const filtersArray: IFilterList[] = [];

    if (stageStatusPair) {
      stageStatusPair.forEach((stage) => {
        stageTags.add(stage.applicationStage);
        if (stage.applicationState) {
          statusTags.add(stage.applicationState);
        }
      });
    }
    if (stageTags.size > 0) {
      const stageTag = `Application Stage: ${Array.from(stageTags).join(', ')}`;
      filtersArray.push({
        tag: stageTag,
        id: 'applicationStage',
      });
    }

    if (statusTags.size > 0) {
      const statusTag = `Application Status: ${Array.from(statusTags).join(', ')}`;
      filtersArray.push({
        tag: statusTag,
        id: 'applicationStatus',
      });
    }
    setStageStatusFilters(filtersArray);
  };

  const pushBusinessDefinitionTag = (definitionTag: string | null | undefined) => {
    const filtersArray: IFilterList[] = [];
    if (definitionTag != undefined && !isEmpty(definitionTag))
      filtersArray.push({
        tag: `Business Definition: ${definitionTag}`,
        id: 'businessDefinitionName',
      });

    setBusinessDefinitionFilters(filtersArray);
  };

  const changeQuery = (value: number) => {
    const updatedQuery = { ...query };
    if (value == 1) {
      updatedQuery.submittedByRecruiterId = userContext?.email;
    } else {
      updatedQuery.submittedByRecruiterId = '';
    }
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
  };

  const handleDownload = async (type: string) => {
    setOpenDownload(true);
    setType(type);
  };

  const downloadCandidates = async (v: string[]) => {
    const downloadStatus = await handleCandidateDownload(type, query, v);
    if (downloadStatus == 'success') setNotification(success('Downloaded candidates'));
    else setNotification(warning('Something went wrong, Please try again.'));
    setOpenDownload(false);
  };

  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    submissionAmplitudeEvent(
      !candidate.isSelected ? 'Submission_candidate_Select' : 'Submission_candidate_UnSelect',
      'Checkbox'
    );
    if (!candidate.isSelected) setNumberOfSelectedCandidates(numberOfSelectedCandidates + 1);
    else setNumberOfSelectedCandidates(numberOfSelectedCandidates - 1);
    candidate.isSelected = !candidate.isSelected;
  };
  const selectAllCandidates = () => {
    data.list
      .filter((candidate) => candidate.email && !candidate.isInDnc)
      .forEach((candidate) => {
        candidate.isSelected = true;
      });
    setNumberOfSelectedCandidates(data.list.length);
  };

  const submissionEvent = {
    ['Email']: () => submissionAmplitudeEvent('Submission_candidate_Copy Email', 'Email'),
    ['SMS']: () => submissionAmplitudeEvent('Submission_candidate_Send SMS', 'SMS'),
    ['Update Status']: () =>
      submissionAmplitudeEvent('Submission_candidate_Update_Status', 'UpdateStatus'),
  };

  const unselectAllCandidates = () => {
    data.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };

  const onCVClick = (index: number) => {
    submissionAmplitudeEvent('Submission_View_Submission_submission', 'View Submission'),
      setSearchQuery(query.query ?? '');
    setCvViewSourcePage(ITrackCvPages.SUBMISSION);
    updateFromPage('');
    updateView('submission');
    setCvIndex(index);
    // openModal(data.list[index]);
  };

  const handleOpenSingleEmail = (candidate, email) => {
    updateFromPage('');
    openModal(candidate);
    setSelectedEmail(email);
  };
  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setQuery({ ...query });
  };

  const [candidateToShare, setCandidateToShare] = useState<ICandidate | null>();
  const [selectedCandidateEntitys, setSelectedCandidateEntitys] = useState<
    ICandidateRecentConversation[]
  >([]);
  const openShare = (candidate: ICandidate & { isSelected: boolean }) => {
    setOpenShareCandidateEmail(true);
    setCandidateToShare(candidate);
    setSelectedCandidateEntitys([data.stageStatus[data.list.indexOf(candidate)]]);
  };

  const onShareEmailClick = () => {
    setCandidateToShare(null);
    setOpenShareCandidateEmail(true);
    const selectedEntitys = data.list
      .map((candidate, index) => ({ isSelected: candidate.isSelected, index: index }))
      .filter((candidate) => candidate.isSelected)
      .map((item) => data.stageStatus[item.index]);

    setSelectedCandidateEntitys(selectedEntitys);
  };
  const [delIntro, setDelIntro] = useState<ICandidate>();
  const deleteIntraction = async () => {
    if (!isEmpty(delIntro) && delIntro != undefined) {
      try {
        setOpenDelete(false);
        await deleteSubmission(delIntro);
        setNotification({
          message: 'Sucessfully deleted the submission',
          type: 'success',
        });
        setQuery({ ...query });
      } catch (e) {
        setNotification({
          message: 'Unable to delete the interaction, please update the candidate and try again',
          type: 'warning',
        });
      }
    } else {
      setNotification({
        message: 'please select submission',
        type: 'warning',
      });
      setOpenDelete(false);
    }
  };

  const onClearFilters = () => {
    submissionAmplitudeEvent('Submission_Clear', 'Clear');
    setSearchParams({ d: getEncodedQueryParams(SubmissionListingDefaultQuery) });
    setAppliedFilters([]);
    setStageStatusFilters([]);
    setAdvancedFilterFields(setDefaultAdvanceFilters(SubmissionListingDefaultQuery));
    setQuery(SubmissionListingDefaultQuery);
    setSearchFilterDefaultValues({
      query: '',
      candidateStatus: null,
    });
  };

  const updateStageStatus = () => {
    setStageStatusFilters([]);
    setBusinessDefinitionFilters([]);
    setAdvancedFilterFields((prev) => ({ ...prev, businessDefinitionName: '' }));
    const updatedQuery = { ...query };
    updatedQuery.currentStageCurrentStatusPairs = [];
    updatedQuery.candidateJobStatus = query.jobStatusUpdatedByADVFilter
      ? query.candidateJobStatus
      : 'Any';
    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
  };

  const updateBusinessStageStatus = (data: string, count: number) => {
    const selectedStageStatus = JSON.parse(data);
    setStageStatusFilters([]);
    const oldStatus = query.stageStatusPairs;
    const updatedQuery = { ...query };
    updatedQuery.businessDefinitionData = selectedStageStatus.stageStatusData;
    updatedQuery.candidateStageStatusPipelineData = '';
    let candidateJobStatusByBDA = 'A';
    count == 0
      ? (updatedQuery.currentStageCurrentStatusPairs = [
          {
            applicationStage: 'Dummy',
            applicationState: 'Dummy',
          },
        ])
      : (updatedQuery.currentStageCurrentStatusPairs = [
          ...(oldStatus ?? []),
          ...selectedStageStatus.stageStatusData,
        ]);
    if (!updatedQuery.jobStatusUpdatedByADVFilter || candidateJobStatusByADVALL) {
      if (
        selectedStageStatus.jobOption == 'open_jobs' ||
        selectedStageStatus.jobOption == 'closed_jobs'
      )
        updatedQuery.candidateJobStatus =
          selectedStageStatus.jobOption === 'open_jobs'
            ? 'Opened'
            : selectedStageStatus.jobOption === 'closed_jobs'
            ? 'Closed'
            : 'Any';
      else updatedQuery.candidateJobStatus = 'Any';
    }
    if (
      selectedStageStatus.jobOption == 'open_jobs' ||
      selectedStageStatus.jobOption == 'closed_jobs'
    )
      candidateJobStatusByBDA =
        selectedStageStatus.jobOption === 'open_jobs'
          ? 'Opened'
          : selectedStageStatus.jobOption === 'closed_jobs'
          ? 'Closed'
          : 'Any';
    else candidateJobStatusByBDA = 'Any';

    setCandidateJobStatusByBD(
      updatedQuery.jobStatusUpdatedByADVFilter
        ? candidateJobStatusByBDA
        : updatedQuery?.candidateJobStatus ?? 'notSelected'
    );

    setSearchParams({ d: encodeQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    const advancedFilterDefaults: SubmissionAdvancedFilterFormProps = advancedFilterDefaultValues;
    advancedFilterDefaults.businessDefinitionName = selectedStageStatus.businessDefinitionName;
    setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
  };

  const convertToPlacement = (candidate: ICandidate) => {
    setSelectedSubCandidate(candidate);
    setOpenPlacementDrawer(true);
  };

  const selectedBulkCandidateIndex = data.list.findIndex((candidate) => candidate.isSelected);

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <LayoutTheme>
        <AppLayoutTheme>
          <WsTitleBanner
            title={title}
            goBack={() => navigateBack('/')}
            rightChildren={
              <Grid xs={6} container justifyContent="space-between" alignItems="center" spacing={0}>
                <Grid item xs={11} justifyContent="flex-end">
                  <Box display="flex" justifyContent="flex-end">
                    <FormControlLabel
                      sx={{
                        padding: 0,
                        margin: 0,
                        fontWeight: 900,
                        fontSize: '14px',
                        color: WsColors.TheBlues500,
                        '& .MuiFormControlLabel-label': {
                          marginRight: 1,
                        },
                      }}
                      control={
                        <Switch
                          disabled={false}
                          size="medium"
                          checked={false}
                          onChange={() => {
                            navigateBack('/all-submissions');
                            amplitude('Submissions_candidate_View_More', {
                              clickedOn: 'View More',
                              pageName: 'Submissions',
                              pageUrl: window.location.href,
                            });
                          }}
                          sx={{
                            '& .MuiSwitch-track': {
                              backgroundColor: 'whitesmoke',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: WsColors.Red500,
                            },
                          }}
                        />
                      }
                      label="New View"
                      labelPlacement="start"
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Tooltip title="Tour to job Section">
                    <IconButton onClick={() => tour?.start()}>
                      <InfoIcon style={{ color: WsColors.White, fontSize: 37 }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            }
          />
        </AppLayoutTheme>
        <ScrollableTabs
          scrollList={submissionTabsList}
          getTabIndex={(index: number) => {
            setTitle(submissionTabsList[index].tabName);
            changeQuery(index);
          }}
          variant={TabTextVariant.SUB_TITLE}
        />
        <Box my={2}>
          <Grid container>
            <Grid item xs={3}>
              <SubmissionListSideNav
                setSelectedStageStatus={selectedStageStatus}
                selectedIndexValue={selectedIndex}
                stageStats={stageStats}
                totalCandidateCount={allCandidatesCount}
                isStageStatus={(value) => setIsStageStatus(value)}
                updateStageStatus={updateStageStatus}
                updateBusinessStageStatus={updateBusinessStageStatus}
                deleteBDFilterIndex={deleteBDFilterIndex}
                deleteCSFilterIndex={deleteCSFilterIndex}
                allStageStats={allStageStats}
                openStageStats={openStageStats}
                closeStageStats={closeSageStats}
                allCandidatesCountForBD={allCandidatesCountForBD}
              />
            </Grid>
            <Grid item xs={9}>
              <Box>
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <SubmissionListingSearchFilter
                    onSort={onSort}
                    onSearch={onSearch}
                    onApplyFilters={onApplyFilters}
                    handleDownload={handleDownload}
                    defaultValues={advancedFilterDefaultValues}
                    clearAdvanceFilters={onClearFilters}
                    searchFilterdefaultValues={searchFilterDefaultValues}
                    isStageStatus={isStageStatus}
                  />
                </ErrorBoundary>
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <WsFilterTags
                    filtersList={stageStatusFilters}
                    onDelete={onDeletestageStatusFilterTag}
                    label={'Candidate Status'}
                  />
                  <WsFilterTags
                    filtersList={businessDefinitionFilters}
                    onDelete={onDeleteBusinessFilterTag}
                    label={'Business Definition'}
                  />
                  <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
                </ErrorBoundary>
              </Box>

              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {data.list.length > 0 && (
                  <Box py={2}>
                    <WsPagination
                      count={Math.ceil(
                        totalPageCount /
                          Number(paginationSettings?.submittedCandidatesPagination ?? 50)
                      )}
                      onChange={pageChange}
                      itemsPerPage={Number(paginationSettings?.submittedCandidatesPagination ?? 50)}
                      pageNumber={query.page}
                      defaultPage={pageNumber == 0 ? 1 : pageNumber}
                      isPageCount={true}
                      type="Candidates"
                      totalCount={data.pagesCount}
                      textCursorType="text"
                    />
                  </Box>
                )}
              </ErrorBoundary>

              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {data.list.length > 0 && !loader && !data.isError ? (
                  <Box>
                    <Box>
                      {data.list.map((candidate, index) => {
                        const closureId = closures?.find(
                          ({ submissionId }) => submissionId === (candidate.id as string)
                        )?.id;

                        return (
                          <Box key={index} pt={2}>
                            <WsCandidateCard
                              onTitleClick={() =>
                                openUrlInNewTab(`/jobs-crm/${candidate.jobId}/false/1/pipeline`)
                              }
                              array={data.list.map((i) => i.candidateId ?? '')}
                              source="Submission"
                              isCheckBox={true}
                              candidate={candidate}
                              onCVClick={onCVClick}
                              pageRefresh={() => setQuery({ ...query })}
                              onActionClick={(index) => setCvIndex(index)}
                              candidateIndex={index}
                              checkBoxOnclick={() => addSelectedCandidate(candidate)}
                              gridXs={3}
                              isFrom="SubmissionListing"
                              isActiveCard={true}
                              openSingleEmail={handleOpenSingleEmail}
                              // interaction={getInteraction(data.stageStatus, candidate.email)}
                              stageStatus={{
                                applicationStage: candidate?.applicationStage,
                                applicationState: candidate?.applicationState,
                              }}
                              amplitudeCallBack={(event) => submissionEvent[event]()}
                              rightChildren={
                                <>
                                  {candidate.vmsProcess !== 'SUCCESS' &&
                                    userContext?.email &&
                                    vmsFeatureAllowedEmails.includes(userContext?.email) && (
                                      <Tooltip title="Submit to VMS">
                                        <IconButton onClick={() => setSelectedCandidate(candidate)}>
                                          <FileUploadOutlinedIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  <Tooltip title="Email Profile">
                                    <IconButton
                                      onClick={() => {
                                        openShare(candidate);
                                        submissionAmplitudeEvent(
                                          'Submissions_candidate_Email_profile',
                                          'Email Profile'
                                        );
                                      }}
                                    >
                                      <MailOutlineIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Box mt={1}>
                                    <WsElementWithPopOver element={<WsIconMoreVer />}>
                                      <Stack p={2} columnGap={1}>
                                        {workspace.owns && (
                                          <Tooltip title=" delete Submission">
                                            <WsIconText
                                              text="Delete Submission"
                                              onClick={() => {
                                                setDelIntro(candidate);
                                                setOpenDelete(true);
                                              }}
                                              icon={<DeleteForever />}
                                              textSize={WsTypoVariants.BODY1}
                                              textCursor="pointer"
                                              textColor={WsTypoColors.BLACK}
                                            />
                                          </Tooltip>
                                        )}
                                        <Box mt={1}>
                                          {closureId ? (
                                            <>
                                              {isAuthorizedViewPlacement && (
                                                <WsIconText
                                                  text="Candidate Placed"
                                                  onClick={() =>
                                                    routerNavigate(`/placements/${closureId}`)
                                                  }
                                                  icon={<CgArrowsExchangeAlt size={24} />}
                                                  textSize={WsTypoVariants.BODY1}
                                                  textCursor="pointer"
                                                  textColor={WsTypoColors.BLACK}
                                                />
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              {isAuthorizedAddPlacement && (
                                                <>
                                                  <WsIconText
                                                    text="Convert To Permanent"
                                                    onClick={() => convertToPlacement(candidate)}
                                                    icon={<CgArrowsExchangeAlt size={24} />}
                                                    textSize={WsTypoVariants.BODY1}
                                                    textCursor="pointer"
                                                    textColor={WsTypoColors.BLACK}
                                                  />
                                                </>
                                              )}
                                            </>
                                          )}
                                        </Box>
                                      </Stack>
                                    </WsElementWithPopOver>
                                  </Box>
                                </>
                              }
                            />
                          </Box>
                        );
                      })}
                    </Box>
                    {pageNumber * Number(paginationSettings?.submittedCandidatesPagination ?? 50) ==
                      500 && (
                      <ErrorBoundary
                        FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                      >
                        <Box pt={2}>
                          <NoDataMsg
                            title="You've reached the viewing limit of 500 results."
                            listTitle="Try the following to refine your search :"
                            msg={`To help you find what you're looking for more efficiently and ensure optimal performance, the number of accessible results is limited.`}
                            suggestionsList={[
                              'Use Specific Keywords',
                              'Apply Filters',
                              'Modify Your Query',
                              'Sort Results',
                            ]}
                          />
                        </Box>
                      </ErrorBoundary>
                    )}
                  </Box>
                ) : loader ? (
                  <WsListingLoader />
                ) : data.isError ? (
                  <Box m={2}>
                    <NoDataFoundCard text={apiFailedMessage} />
                  </Box>
                ) : (
                  <Box my={2}>
                    <NoDataFoundCard text="No Candidates Found" />
                  </Box>
                )}
              </ErrorBoundary>
              <Grid item xs={12} container justifyContent="flex-end" mt={2} mr={1.75}>
                <WsPagination
                  count={Math.ceil(
                    totalPageCount / Number(paginationSettings?.submittedCandidatesPagination ?? 50)
                  )}
                  onChange={pageChange}
                  itemsPerPage={Number(paginationSettings?.submittedCandidatesPagination ?? 50)}
                  pageNumber={query.page}
                  defaultPage={pageNumber == 0 ? 1 : pageNumber}
                  isPageCount={false}
                  type="Candidates"
                  totalCount={data.pagesCount}
                  textCursorType="text"
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {numberOfSelectedCandidates > 0 && (
            <CandidateBulkSelectionBar
              selectedCandidates={numberOfSelectedCandidates}
              selectAllCandidates={selectAllCandidates}
              onShareEmailClick={onShareEmailClick}
              unselectAllCandidates={unselectAllCandidates}
              openBulkSms={() => setOpenBulkSms(true)}
              showSendEmail={false}
              openEmail={() => {
                updateFromPage('');
                setOpenEmail(true);
              }}
              onUpdateClick={
                updateAllowed
                  ? () => {
                      submissionAmplitudeEvent('BULK_UPDATE_SUBMISSIONS_INITIATE', 'Update Button');
                      if (numberOfSelectedCandidates == 1) openUpdateStatusModal();
                      else if (numberOfSelectedCandidates > 1) openBulkUpdateStatusModal();
                    }
                  : undefined
              }
            />
          )}
        </ErrorBoundary>

        {openBulkStatusModal && (
          <BulkUpdateStatusDrawer
            candidates={data.list.filter((i) => i.isSelected)}
            openStatusModal={openBulkStatusModal}
            onClose={closeBulkUpdateStatusModal}
            refreshAll={() => setQuery({ ...query })}
            onUpdateAllClick={() => {
              submissionAmplitudeEvent('BULK_UPDATE_SUBMISSIONS_EXECUTE', 'Update All Button');
              closeBulkUpdateStatusModal();
            }}
          />
        )}

        {openStatusModal && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <StageStatusContextProvider>
              <UpdateStatusModal
                candidate={data.list[selectedBulkCandidateIndex]}
                onCVClick={onCVClick}
                candidateIndex={selectedBulkCandidateIndex}
                openStatusModal={openStatusModal}
                onClose={closeUpdateStatusModal}
                updateStageStatus={() => setQuery({ ...query })}
                jobFieldSettings={jobFieldSettings}
              />
            </StageStatusContextProvider>
          </ErrorBoundary>
        )}

        {openBulkSms && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <BulkSmsDrawer
              open={openBulkSms}
              handleClose={() => {
                setOpenBulkSms(false);
                handleSelectedCandidatesLimit(data);
                setNumberOfSelectedCandidates(0);
              }}
              candidates={data.list.filter((candidate) => candidate.isSelected)}
            />
          </ErrorBoundary>
        )}
        {openEmail && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <BulkEmail
              ignoreDuplicateCheck={true}
              open={openEmail}
              handleClose={() => {
                setOpenEmail(false);
                handleSelectedCandidatesLimit(data);
                setNumberOfSelectedCandidates(0);
              }}
              candidates={data.list.filter(
                (candidate) => candidate.isSelected && !candidate.isInDnc
              )}
              drawer={true}
            />
          </ErrorBoundary>
        )}
        {openShareCandidateEmail && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <ShareCandidateProfileEmail
              selectedCandidateEntitys={selectedCandidateEntitys}
              candidatesList={
                candidateToShare != null
                  ? [candidateToShare]
                  : data.list.filter(
                      (candidate) =>
                        candidate.isSelected &&
                        !candidate.isInDnc &&
                        (candidate.downloadSource = 'BULK_SUBMISSION')
                    )
              }
              open={openShareCandidateEmail}
              handleClose={() => {
                setOpenShareCandidateEmail(false);
                unselectAllCandidates();
              }}
            />
          </ErrorBoundary>
        )}
        <WsDialog openDialog={openDelete} onClose={() => setOpenDelete(false)}>
          <ConfirmationDialog
            text="Deleted submission cannot be revoked, are you sure want to delete the submission?"
            title="Delete Mapping"
            confirm={() => deleteIntraction()}
            close={() => setOpenDelete(false)}
          />
        </WsDialog>
      </LayoutTheme>
      {selectedCandiate != null && (
        <UploadToVMS candidate={selectedCandiate} onClose={() => setSelectedCandidate(null)} />
      )}
      <WsDrawer
        title="Download Fields"
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        size="medium"
        headerSize="medium"
      >
        <Box pb={5}>
          <DownloadField
            selectedFields={(d) => downloadCandidates(d)}
            close={() => setOpenDownload(false)}
          />
        </Box>
      </WsDrawer>
      {openPlacementDrawer && (
        <ConvertPlacement
          open={openPlacementDrawer}
          onClose={() => setOpenPlacementDrawer(false)}
          candidate={selectedSubCandiate}
        ></ConvertPlacement>
      )}
    </ErrorBoundary>
  );
}
